/**
 * Razorpay Standard Checkout, in one place.
 *
 * This used to live only inside BillingTab, which is why the paywall's
 * "Upgrade to Voyager" could not open it and navigated the user to the Billing
 * tab to start again instead — two extra clicks and a lost intent, at exactly
 * the moment they had decided to pay.
 *
 * Both surfaces now call `startPlanCheckout`. The order of operations is
 * deliberate and worth keeping: the server creates the order/subscription (so
 * the amount, the plan and the owner are server-authored and can't be edited
 * by the client), Razorpay collects the money, and the server verifies the
 * HMAC before any access is granted.
 */

import { apiService, BillingPlan } from '../services/apiService';
import { trackClick } from './analytics';

declare global {
  interface Window { Razorpay?: any }
}

/**
 * What happened inside the Razorpay sheet, logged as ordinary clicks.
 *
 * Until 2026-09-24 the sheet was a black box: we saw the paywall click and,
 * sometimes, a payment in Razorpay — nothing in between. Of 21 customers most
 * never paid, and whether each one closed the sheet, had a card declined or
 * paid and then failed verification could only be pieced together by hand
 * from the gateway dashboard. These five events (opened, dismissed, payment
 * failed, verified, verify failed) close that gap; the admin Checkout funnel
 * reads them from site_events to place each person's drop-off.
 *
 * Shared by both surfaces (the paywall via startPlanCheckout and the Billing
 * tab's own openCheckout) so the two log the same shape. Props are kept to
 * ids and short strings: the store truncates a props blob at 4 KB.
 */
export interface CheckoutTrace {
  flow: 'subscription' | 'order';
  plan: string;
  /** Minor units of `currency` — what the sheet is expected to charge. */
  amount?: number | null;
  currency?: string | null;
  method?: string | null;
  subscriptionId?: string | null;
  orderId?: string | null;
  trial?: boolean;
  /** Which screen opened the sheet: 'paywall' or 'billing_tab'. */
  surface: string;
}

const clip = (v: any, n = 200): string | null =>
  (v === null || v === undefined || v === '') ? null : String(v).slice(0, n);

export function checkoutTracker(t: CheckoutTrace) {
  const ids = {
    ...(t.subscriptionId ? { subscription_id: t.subscriptionId } : {}),
    ...(t.orderId ? { order_id: t.orderId } : {}),
  };
  const base = { flow: t.flow, plan: t.plan, surface: t.surface, ...ids };
  // Analytics must never break a payment: every call is fenced.
  const fire = (action: string, props: Record<string, unknown>) => {
    try { trackClick(action, props); } catch { /* ignore */ }
  };
  return {
    opened: () => fire('checkout_opened', {
      ...base,
      amount: t.amount ?? null, currency: t.currency || null,
      method: t.method || null, trial: !!t.trial,
    }),
    dismissed: () => fire('checkout_dismissed', base),
    /** Razorpay's payment.failed payload: {error: {code, description,
     *  source, step, reason, metadata: {payment_id, order_id}}}. */
    paymentFailed: (r: any) => {
      const e = r?.error || {};
      fire('checkout_payment_failed', {
        ...base,
        code: clip(e.code, 60), description: clip(e.description),
        reason: clip(e.reason, 80), source: clip(e.source, 40), step: clip(e.step, 60),
        payment_id: clip(e.metadata?.payment_id, 40),
      });
    },
    verified: (paymentId?: string) => fire('checkout_verified', {
      ...base, payment_id: clip(paymentId, 40),
    }),
    verifyFailed: (error: any, paymentId?: string) => fire('checkout_verify_failed', {
      ...base, error: clip(error), payment_id: clip(paymentId, 40),
    }),
    /** The confirmation failed but the membership turned up anyway (the
     *  Razorpay webhook granted it) — see waitForMembership. */
    verifiedLate: (paymentId?: string) => fire('checkout_verified_late', {
      ...base, payment_id: clip(paymentId, 40),
    }),
  };
}

const PAID_STATES = ['voyager', 'voyager_assigned'];
/** Waits between re-checks: ~45 s in all, longer than an API restart. */
const RECHECK_MS = [3000, 5000, 5000, 5000, 7000, 10000, 10000];

/**
 * After Razorpay took the money but our confirmation call failed (on
 * 2026-10-28 it hit the half-minute the API was restarting), ask the billing
 * summary a few times whether the membership is live anyway: the Razorpay
 * webhook grants it on its own, usually within a minute. True as soon as it
 * is; false if it never shows up. Each successful summary also reaches App
 * through BILLING_SUMMARY_EVENT, so the upgrade prompts step aside with it.
 */
export async function waitForMembership(userId: string, trial = false): Promise<boolean> {
  const want = trial ? [...PAID_STATES, 'trial_active'] : PAID_STATES;
  for (const ms of RECHECK_MS) {
    await new Promise(r => setTimeout(r, ms));
    const sum = await apiService.getBillingSummary(userId);
    const state = (sum.data as any)?.state;
    if (sum.success && sum.data?.success && want.includes(state)) return true;
  }
  return false;
}

let razorpayLoader: Promise<boolean> | null = null;

/** Inject checkout.js once, shared across every caller. */
export const loadRazorpay = (): Promise<boolean> => {
  if (window.Razorpay) return Promise.resolve(true);
  if (!razorpayLoader) {
    razorpayLoader = new Promise(resolve => {
      const el = document.createElement('script');
      el.src = 'https://checkout.razorpay.com/v1/checkout.js';
      el.onload = () => resolve(true);
      // Null the cache on failure so a later attempt can retry rather than
      // resolving false forever from one flaky network moment.
      el.onerror = () => { razorpayLoader = null; resolve(false); };
      document.body.appendChild(el);
    });
  }
  return razorpayLoader;
};

export interface PlanCheckoutOptions {
  userId: string;
  plan: BillingPlan;
  /** 'subscription' = yearly auto-renew; 'order' = one year, no renewal. */
  flow?: 'subscription' | 'order';
  email?: string;
  name?: string;
  coupon?: string;
  /** Payment method the discount is tied to ('card' | 'upi'). A Razorpay
   *  subscription offer covers one method, so it has to be known before the
   *  subscription is created, and checkout is pinned to it. */
  payMethod?: string;
  /** Start as a trial: the subscription is created with its first charge
   *  deferred, so the sheet collects the mandate and nothing is billed until
   *  the trial ends. Only valid where the server says trial.mode is
   *  'mandate'; it forces the subscription flow. */
  trial?: boolean;
  /** Fired after the SERVER confirms the payment, not when Razorpay closes. */
  onSuccess?: () => void;
  onError?: (message: string) => void;
  /** Fired when the user dismisses the sheet, and on failure — for busy state. */
  onSettled?: () => void;
}

/**
 * Create the order/subscription server-side, open Razorpay, verify the result.
 * Resolves once the sheet has been opened (or an error reported); the outcome
 * arrives through the callbacks.
 */
export async function startPlanCheckout(opts: PlanCheckoutOptions): Promise<void> {
  const { userId, plan } = opts;
  const fail = (m: string) => { opts.onError?.(m); opts.onSettled?.(); };

  if (!userId) return fail('Please sign in again to continue.');

  // Auto-renew is INR-only on this Razorpay account, so defaulting every
  // caller to 'subscription' meant a USD customer clicked Upgrade, waited, and
  // got an alert telling them recurring billing is unavailable — with no way
  // to pay at all. When the caller has not pinned a flow, ask the server which
  // one this account can actually use and take the one-time path otherwise.
  let flow = opts.flow;
  // A trial is by definition recurring — there is nothing to defer on a
  // one-time order — so it pins the flow rather than asking.
  if (opts.trial) flow = 'subscription';
  if (!flow) {
    const sum = await apiService.getBillingSummary(userId);
    const d: any = sum.data;
    // Unreachable summary falls back to subscription, the behaviour before
    // this existed: better to fail the way it always did than to silently
    // charge a non-renewing year to someone who wanted a subscription.
    flow = (sum.success && d && d.auto_renew_available === false)
      ? 'order' : 'subscription';
  }

  const res = flow === 'subscription'
    // The coupon was being dropped here: only the order flow forwarded it, so
    // a code applied on the pricing modal silently bought at full price.
    ? await apiService.billingSubscribe(userId, plan.code,
                                        opts.coupon || undefined,
                                        opts.payMethod || undefined,
                                        !!opts.trial)
    : await apiService.billingOrder(userId, plan.code, opts.coupon || undefined);

  if (!res.success || !res.data?.success) {
    return fail(res.error || 'Could not start the payment. Please try again.');
  }
  const d: any = res.data;

  // The API returns razorpay_subscription_id / razorpay_order_id / amount_paise.
  // Reading the shorter names left BOTH undefined, so Checkout received neither
  // a subscription nor an order and fell back to a bare amount-less payment —
  // which Razorpay renders at its ₹1 minimum. Hence "₹1" from the paywall while
  // the Billing tab, which reads the right keys, showed ₹9,000.
  const subscriptionId: string | undefined = d.razorpay_subscription_id;
  const orderId: string | undefined = d.razorpay_order_id;
  const amountPaise: number | undefined = d.amount_paise;

  if (!subscriptionId && !orderId) {
    return fail('Could not start the payment — the server did not return a '
      + 'subscription or order to charge against.');
  }

  const ok = await loadRazorpay();
  if (!ok || !window.Razorpay) {
    return fail('Could not load the payment window — check your connection and try again.');
  }

  const trace = checkoutTracker({
    flow, plan: plan.code,
    // An order carries its charged amount; a subscription only its plan's.
    amount: amountPaise ?? d.plan?.amount_paise ?? null,
    currency: d.currency || null,
    method: opts.payMethod || null,
    subscriptionId, orderId, trial: !!opts.trial,
    surface: 'paywall',
  });

  const rz = new window.Razorpay({
    key: d.key_id,
    name: 'UniversityTech.io',
    description: `${plan.name} — ${flow === 'subscription' ? 'yearly auto-renew' : 'one year, no auto-renewal'}`,
    prefill: { email: opts.email || '', name: opts.name || '' },
    notes: { utech_user_id: userId },
    ...(subscriptionId ? { subscription_id: subscriptionId } : {}),
    ...(orderId ? { order_id: orderId, amount: amountPaise, currency: d.currency || 'INR' } : {}),
    // Pin the method the offer covers, so the customer cannot pick another
    // one and be charged full price after seeing the discounted figure.
    ...(opts.payMethod ? {
      method: {
        card: opts.payMethod === 'card',
        upi: opts.payMethod === 'upi',
        netbanking: false, wallet: false, emi: false, paylater: false,
      },
    } : {}),
    theme: { color: '#8f7b46' },
    handler: async (resp: any) => {
      const v = await apiService.billingVerify({
        user_id: userId,
        flow,
        plan_code: plan.code,
        razorpay_payment_id: resp.razorpay_payment_id,
        razorpay_signature: resp.razorpay_signature,
        razorpay_order_id: resp.razorpay_order_id,
        razorpay_subscription_id: resp.razorpay_subscription_id,
      });
      if (v.success && v.data?.success) {
        trace.verified(resp.razorpay_payment_id);
        opts.onSuccess?.();
      } else {
        trace.verifyFailed(v.error || (v.data as any)?.detail || 'verify refused',
                           resp.razorpay_payment_id);
        // Razorpay has the money; the webhook may well have granted the plan.
        if (await waitForMembership(userId, !!opts.trial)) {
          trace.verifiedLate(resp.razorpay_payment_id);
          opts.onSuccess?.();
        } else {
          opts.onError?.(v.error || (v.data as any)?.detail
            || 'Payment could not be verified. If you were charged, contact info@universitytech.io.');
        }
      }
      opts.onSettled?.();
    },
    modal: {
      ondismiss: () => {
        trace.dismissed();
        opts.onSettled?.();
      },
    },
  });

  rz.on('payment.failed', (r: any) => {
    trace.paymentFailed(r);
    opts.onError?.(r?.error?.description || 'Payment failed — you have not been charged.');
    opts.onSettled?.();
  });

  rz.open();
  trace.opened();
}
